import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyRoomMessage,
  blockedBecause,
  type ChatMessage,
  describeChannel,
  type LineupEntry,
  type Lot,
  lotsByListing,
  type OutputOf,
} from '@going-once/client';
import { bidsChannel, roomChannel, type RealtimeMessage } from '@going-once/contracts/channels';
import { cssVar } from '@going-once/tokens';
import { api } from '../lib/api';
import { useChannel } from '../lib/realtime';
import { useStage } from '../lib/stage';
import { describeError, useAsync } from '../lib/useAsync';
import { Box, Card, Field, Pressable, Select, Stack, Text } from '../ui/primitives';
import { ErrorNote, Loading } from '../ui/state';
import { Countdown } from './Auction';
import { formatCents } from './Feed';

/**
 * How many of the seller's listings the studio loads — the capability's own ceiling.
 *
 * **This is the interface's limit, not a tuning choice, and it does not scale.** A seller
 * with five thousand listings gets the first hundred in a dropdown, which is unusable
 * twice over: the control is wrong for the volume, and everything past the first page is
 * invisible rather than merely far away. Fixing it needs a **seller-side listing search**
 * that does not exist — `catalog.listing.list` filters by status and pages by cursor, and
 * `discovery.listing.browse` is the guest feed, which has no text search yet.
 * Until that capability exists the control tells the seller what it is showing them
 * rather than implying it is everything.
 */
const LISTING_PAGE = 100;

/**
 * The broadcast desk: schedule a show, plan its lineup, go on air, drop the hammer.
 *
 * **Deliberately one screen with everything on it.** A seller running a show has one
 * hand on the camera and is talking to an audience; a console that hides the lineup
 * behind a tab is a console nobody uses live. The real studio (Lane G, on the phone) will
 * be laid out properly — this is the desk that proves the path end to end.
 *
 * **The camera preview is local and muted.** The seller's own audio played back at them
 * is a feedback loop, and the one that happens in a room with speakers is loud.
 */
export function Studio({
  showId,
  onOpenShow,
  onOpenStudioShow,
  onBack,
}: {
  showId?: string;
  onOpenShow: (showId: string) => void;
  onOpenStudioShow: (showId: string) => void;
  onBack: () => void;
}) {
  if (showId) return <Desk showId={showId} onOpenShow={onOpenShow} onBack={onBack} />;
  return <ShowList onOpenStudioShow={onOpenStudioShow} />;
}

/** The seller's shows, and the form that makes one. */
function ShowList({ onOpenStudioShow }: { onOpenStudioShow: (showId: string) => void }) {
  const shows = useAsync(() => api().query('shows.show.list', { limit: 25 }), []);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();

  async function schedule() {
    setBusy(true);
    setError(undefined);
    try {
      const { show } = await api().command(
        'shows.show.schedule',
        // Now, not a picked time: the demo case is "I am about to go live", and a date
        // picker for a show starting in four minutes is friction with no decision in it.
        { title: title.trim(), scheduledStartAt: new Date().toISOString() },
        {},
      );
      setTitle('');
      onOpenStudioShow(show.showId);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap="lg">
      <Text as="h2" step="heading">
        Shows
      </Text>

      <Card>
        <Stack gap="sm">
          <Field label="Show title" id="show-title" value={title} onChange={setTitle} placeholder="Friday denim drop" />
          <Pressable variant="primary" busy={busy} disabled={title.trim().length === 0} onPress={() => void schedule()}>
            Schedule a show
          </Pressable>
          <Text step="label" tone="fg.muted">
            Scheduling provisions nothing. Video starts — and starts costing — when you go live.
          </Text>
        </Stack>
      </Card>

      {error ? <ErrorNote error={error} /> : null}
      {shows.loading ? <Loading what="your shows" /> : null}
      {shows.error ? <ErrorNote error={shows.error} onRetry={shows.reload} /> : null}

      <Stack gap="sm">
        {shows.data?.shows.map((show) => (
          <Card key={show.showId} padding="sm">
            <Stack direction="row" justify="between" align="center" gap="sm" wrap>
              <Stack gap="xs">
                <Text>{show.title}</Text>
                <Text step="label" tone="fg.muted">
                  {show.state} · {show.lineupSize} lots · {new Date(show.scheduledStartAt).toLocaleString()}
                </Text>
              </Stack>
              <Pressable variant="secondary" onPress={() => onOpenStudioShow(show.showId)}>
                Open
              </Pressable>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

/** One show's desk: camera, lineup, hammer. */
function Desk({
  showId,
  onOpenShow,
  onBack,
}: {
  showId: string;
  onOpenShow: (id: string) => void;
  onBack: () => void;
}) {
  const [show, setShow] = useState<OutputOf<'shows.show.get'>['show']>();
  const [lineup, setLineup] = useState<LineupEntry[]>([]);
  const [publisherToken, setPublisherToken] = useState<string>();
  const [live, setLive] = useState<Lot[]>([]);
  const [done, setDone] = useState<Lot[]>([]);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();
  const skew = useRef(0);

  const [chat, setChat] = useState<ChatMessage[]>([]);
  const listings = useAsync(() => api().query('catalog.listing.list', { limit: LISTING_PAGE }), []);
  const stage = useStage({ token: publisherToken, publish: true });

  /**
   * **The seller could not read their own room**, which is most of what a live seller does.
   * The capability to moderate existed with nothing able to call it; this is the caller.
   */
  const room = useChannel(show?.state === 'live' ? roomChannel(showId) : undefined, (message: RealtimeMessage) =>
    setChat((current) => applyRoomMessage(current, message)),
  );

  /**
   * The listings are read once; a lot **selling** is what invalidates them.
   *
   * Held in refs so the two-second poll can reach the current values without depending on
   * them — a poll that re-subscribed whenever a listing changed would restart its own
   * interval. Refreshing on the count of finished lots rather than on a timer means one
   * extra query per hammer fall instead of one every two seconds.
   */
  const reloadListings = useRef(listings.reload);
  reloadListings.current = listings.reload;
  const finishedCount = useRef(0);
  /** One automatic reconnect attempt per mount — see the effect below. */
  const reconnected = useRef(false);

  /**
   * Going on air, and **coming back**.
   *
   * `shows.show.golive` is the same verb for both by design (D48): called on a show that
   * is already live it returns a fresh publisher token for the same stage and fires no
   * second "went live" event. That was built for the dropped-phone case and then never
   * called on mount — so a **page refresh ended the broadcast**. The publisher token
   * lived in component state, the reload discarded it, the stage teardown disconnected
   * the camera, and the studio came back with the show still live, no video, and `End
   * show` as the only button. A seller who refreshed mid-show had no way back on air.
   */
  const goLive = useCallback(async () => {
    const result = await api().command('shows.show.golive', { showId }, {});
    setPublisherToken(result.broadcast.token);
  }, [showId]);

  const loadShow = useCallback(async () => {
    try {
      const { show: fresh, lineup: lots } = await api().query('shows.show.get', { showId });
      setShow(fresh);
      setLineup(lots);
      skew.current = fresh.serverNowMs - Date.now();
    } catch (caught) {
      setError(describeError(caught));
    }
  }, [showId]);

  /**
   * Reconnect once, automatically, when the show is already live and this tab is not
   * broadcasting. Refreshing mid-show is the common case and it should just work.
   *
   * **Once, and only when live.** A retry loop against a credentialed video service is a
   * way to spend money in a tab nobody is looking at, so a failure surfaces as the
   * `Resume broadcast` button rather than as another attempt. A scheduled show is never
   * auto-started: nothing should put a seller on camera who did not ask.
   *
   * **The case this does not handle**, written down rather than discovered: a seller
   * broadcasting from another device who opens the studio here gets a second publisher on
   * the stage rather than a handover. IVS allows twelve, so nothing breaks, but viewers
   * would see both. Detecting it needs a participant read, which the IAM policy
   * deliberately does not grant (D2: presence never comes from the stage).
   */
  // The conversation so far, for a seller opening the desk mid-show — the same backfill
  // the viewer does, and the same reason: the channel remembers nothing.
  useEffect(() => {
    if (show?.state !== 'live') return;
    let cancelled = false;
    void api()
      .query('shows.chat.history', { showId, limit: 50 })
      .then((result) => {
        if (!cancelled) setChat(result.messages.filter((message) => !message.removed));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showId, show?.state]);

  useEffect(() => {
    if (show?.state !== 'live' || publisherToken || reconnected.current) return;
    reconnected.current = true;
    void goLive().catch((caught: unknown) => setError(describeError(caught)));
  }, [show?.state, publisherToken, goLive]);

  /**
   * The lot on the block — **the same arrangement the viewer has**, and for a sharper
   * reason: the person deciding when to drop the hammer must not be reading a price the
   * room saw two seconds ago. The channel says something moved, this re-reads it, and the
   * poll underneath is the backstop.
   */
  const pollLots = useRef<() => void>(() => {});
  const bids = useChannel(show?.state === 'live' ? bidsChannel(showId) : undefined, () => pollLots.current());

  useEffect(() => {
    void loadShow();
    const poll = async () => {
      try {
        const current = await api().query('auctions.auction.current', { showId, limit: 12 });
        setLive(current.live);
        setDone(current.done);
        if (current.done.length !== finishedCount.current) {
          finishedCount.current = current.done.length;
          // A lot just closed: whatever it did to its listing (sold, or back on sale)
          // decides whether the next row offers a button.
          reloadListings.current();
        }
        if (current.live[0]) skew.current = current.live[0].serverNowMs - Date.now();
      } catch {
        // The next tick's problem.
      }
    };
    pollLots.current = () => void poll();
    void poll();
    const timer = setInterval(poll, bids.live ? 15_000 : 2_000);
    return () => clearInterval(timer);
  }, [showId, loadShow, bids.live]);

  async function run(label: string, action: () => Promise<void>) {
    setBusy(label);
    setError(undefined);
    try {
      await action();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(undefined);
    }
  }

  if (!show) return error ? <ErrorNote error={error} /> : <Loading what="the show" />;

  const onAir = show.state === 'live';
  /**
   * Each planned lot against what actually happened to it.
   *
   * **The lineup is a plan and the auction is the fact** (D47), so the join is here
   * rather than in a column of the lineup table — and doing it means a row can say what
   * it is instead of looking untouched. Before this, a lot that had been on the block
   * hours ago rendered exactly like one that had never run, and the only feedback was an
   * error when the seller pressed the button again.
   *
   * Live wins over finished: a relaunched lot has two auctions and the running one is
   * the one the seller is holding a hammer over.
   */
  const lotFor = lotsByListing(live, done);

  const listingFor = new Map((listings.data?.listings ?? []).map((listing) => [listing.listingId, listing]));

  return (
    <Stack gap="lg">
      <Stack direction="row" justify="between" align="center" gap="sm" wrap>
        <Pressable variant="quiet" onPress={onBack}>
          ← Shows
        </Pressable>
        <Stack direction="row" gap="sm">
          {onAir ? (
            <Pressable variant="quiet" onPress={() => onOpenShow(showId)}>
              View as a buyer
            </Pressable>
          ) : null}
          {onAir && !publisherToken ? (
            <Pressable
              variant="primary"
              busy={busy === 'golive'}
              onPress={() =>
                void run('golive', async () => {
                  await goLive();
                  await loadShow();
                })
              }
            >
              Resume broadcast
            </Pressable>
          ) : null}
          {onAir ? (
            <Pressable
              variant="secondary"
              busy={busy === 'end'}
              onPress={() =>
                void run('end', async () => {
                  await api().command('shows.show.end', { showId }, {});
                  setPublisherToken(undefined);
                  stage.leave();
                  await loadShow();
                })
              }
            >
              End show
            </Pressable>
          ) : (
            <Pressable
              variant="primary"
              busy={busy === 'golive'}
              onPress={() =>
                void run('golive', async () => {
                  await goLive();
                  await loadShow();
                })
              }
            >
              {show.state === 'ended' ? 'Show has ended' : 'Go live'}
            </Pressable>
          )}
        </Stack>
      </Stack>

      <Stack gap="xs">
        <Text as="h2" step="heading">
          {show.title}
        </Text>
        <Text step="label" tone="fg.muted">
          {onAir ? (publisherToken ? 'On air' : 'Live · this tab is not broadcasting') : show.state} · {lineup.length}{' '}
          lots
          {/*
            **An operator detail, on the one screen that is a control panel.** Whether the
            realtime channel is carrying traffic is invisible by design on a viewer's
            screen — it falls back to polling and keeps working — which is exactly how a
            broken handshake went unnoticed for a day. Here it is worth saying out loud.
          */}
          {/* Both channels, because they fail independently: the room can be connected
              while the bid channel is not, and a seller reading a stale price is the more
              expensive of the two. */}
          {onAir ? ` · live updates ${describeChannel({ status: bids.status, live: bids.live && room.live })}` : ''}
        </Text>
      </Stack>

      {error ? <ErrorNote error={error} /> : null}
      {stage.error ? <ErrorNote error={{ message: stage.error }} /> : null}

      <Preview stream={stage.local} onAir={onAir} broadcasting={publisherToken !== undefined} status={stage.status} />

      {onAir ? (
        <StudioChat
          messages={chat}
          onRemove={async (message) => {
            await api().command('shows.chat.remove', { showId, messageId: message.messageId, at: message.at }, {});
            setChat((current) => current.filter((entry) => entry.messageId !== message.messageId));
          }}
        />
      ) : null}

      {live.length > 0 ? (
        <Stack gap="sm">
          <Text step="label" tone="fg.muted">
            ON THE BLOCK
          </Text>
          {live.map((lot) => (
            <Card key={lot.auctionId} padding="sm">
              <Stack direction="row" justify="between" align="center" gap="sm" wrap>
                <Text>{lot.title}</Text>
                <Text>
                  {formatCents(lot.currentPriceCents)} · {lot.bidCount} bids ·{' '}
                  <Countdown endTimeMs={lot.endTimeMs} skewMs={skew.current} closed={false} />
                </Text>
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : null}

      <Stack gap="sm">
        <Text step="label" tone="fg.muted">
          LINEUP
        </Text>
        {lineup.length === 0 ? (
          <Text tone="fg.muted">Nothing planned yet. Add a lot below.</Text>
        ) : (
          lineup.map((entry) => (
            <LineupRow
              key={entry.entryId}
              entry={entry}
              showId={showId}
              onAir={onAir}
              {...(lotFor.get(entry.listingId) ?? {})}
              blocked={blockedBecause(listingFor.get(entry.listingId))}
              skewMs={skew.current}
              onChanged={loadShow}
            />
          ))
        )}
      </Stack>

      <AddLot
        showId={showId}
        listings={listings.data?.listings ?? []}
        planned={new Set(lineup.map((entry) => entry.listingId))}
        onAdded={loadShow}
      />
    </Stack>
  );
}

/**
 * The seller's own camera, muted — playing their audio back at them is a feedback loop.
 *
 * **Four states, not two.** "Starting the camera…" used to cover every case where a show
 * was live and no video was flowing, including the one where this tab held no publisher
 * token at all and was never going to start anything. A message that cannot distinguish
 * "working on it" from "not even trying" is how a refreshed studio looked identical to a
 * slow one.
 */
function Preview({
  stream,
  onAir,
  broadcasting,
  status,
}: {
  stream: MediaStream | undefined;
  onAir: boolean;
  broadcasting: boolean;
  status: ReturnType<typeof useStage>['status'];
}) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);

  return (
    <Box background="bg.sunken" radius="lg" style={{ position: 'relative', aspectRatio: '16 / 9', overflow: 'hidden' }}>
      <video
        ref={ref}
        autoPlay
        playsInline
        muted
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: stream ? 'block' : 'none' }}
      />
      {stream ? null : (
        <Stack gap="xs" align="center" justify="center" style={{ height: '100%', padding: cssVar('space.lg') }}>
          <Text tone="fg.muted">
            {!onAir
              ? 'Go live to start the camera. Nothing is published until you do.'
              : !broadcasting
                ? 'This show is live, but this tab is not broadcasting — reconnecting.'
                : status === 'error'
                  ? 'The camera could not start. Resume the broadcast to try again.'
                  : 'Starting the camera…'}
          </Text>
        </Stack>
      )}
    </Box>
  );
}

/**
 * One planned lot, what happened to it, and the hammer.
 *
 * **A row says its own state.** A lot that has run shows what it fetched; a lot on the
 * block shows the clock the room is watching; only a lot that has never run offers the
 * button. That is the fix for the report this came from — a lot auctioned hours earlier
 * looked identical to one nobody had touched, and the only feedback was a refusal after
 * pressing the button.
 */
function LineupRow({
  entry,
  showId,
  onAir,
  lot,
  running,
  blocked,
  skewMs,
  onChanged,
}: {
  entry: LineupEntry;
  showId: string;
  onAir: boolean;
  /** The auction this lot produced, if it has run. Absent means never launched. */
  lot?: Lot;
  running?: boolean;
  /** Why the button is not offered, from the listing's own state. */
  blocked?: string | undefined;
  skewMs: number;
  onChanged: () => Promise<void>;
}) {
  const [seconds, setSeconds] = useState('60');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();

  async function act(label: string, action: () => Promise<unknown>) {
    setBusy(label);
    setError(undefined);
    try {
      await action();
      await onChanged();
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <Card padding="sm">
      <Stack gap="sm">
        <Stack direction="row" justify="between" align="center" gap="sm" wrap>
          <Stack gap="xs">
            <Text>
              {entry.position}. {entry.title}
            </Text>
            {entry.startPriceCents !== undefined ? (
              <Text step="label" tone="fg.muted">
                from {formatCents(entry.startPriceCents)}
              </Text>
            ) : null}
          </Stack>
          <Stack direction="row" gap="sm" align="center">
            {lot ? <Outcome lot={lot} running={running === true} skewMs={skewMs} /> : null}
            {/*
              A sold lot is finished — the listing is gone and the engine would refuse.
              An unsold one is not: "no bids, let's try it again later" is an ordinary
              live-show move, so the button stays next to the outcome rather than the
              outcome replacing it.
            */}
            {blocked ? (
              <Text tone="fg.muted">{blocked}</Text>
            ) : running || lot?.status === 'closed_won' ? null : (
              <>
                <Select
                  label="Seconds"
                  id={`secs-${entry.entryId}`}
                  value={seconds}
                  onChange={setSeconds}
                  options={[
                    { value: '30', label: '30s' },
                    { value: '60', label: '60s' },
                    { value: '120', label: '2m' },
                    { value: '300', label: '5m' },
                  ]}
                />
                <Pressable
                  variant="primary"
                  busy={busy === 'launch'}
                  // The hammer only exists on air: launching a lot into a show nobody is
                  // watching opens a real auction against real inventory.
                  disabled={!onAir}
                  onPress={() =>
                    void act('launch', () =>
                      api().command(
                        'auctions.auction.launch',
                        { listingId: entry.listingId, showId, durationSeconds: Number(seconds) },
                        {},
                      ),
                    )
                  }
                >
                  {lot ? 'Run it again' : 'Put on the block'}
                </Pressable>
              </>
            )}
            <Pressable
              variant="quiet"
              busy={busy === 'remove'}
              onPress={() =>
                void act('remove', () => api().command('shows.lineup.remove', { showId, entryId: entry.entryId }, {}))
              }
            >
              Remove
            </Pressable>
          </Stack>
        </Stack>
        {error ? <ErrorNote error={error} /> : null}
      </Stack>
    </Card>
  );
}

/** Add a listing to the lineup. Planning takes no lease, so anything the seller owns is fair game. */
function AddLot({
  showId,
  listings,
  planned,
  onAdded,
}: {
  showId: string;
  listings: OutputOf<'catalog.listing.list'>['listings'];
  planned: Set<string>;
  onAdded: () => Promise<void>;
}) {
  /**
   * Runnable lots first, then the rest grayed out with the reason in the label.
   *
   * **Shown but unpickable**, not hidden: "why is my item not in the list" is a worse
   * question than "why is my item grayed out", and the second answers itself. Sorting
   * puts everything a seller can actually do at the top, which is the only thing that
   * keeps a picker usable as the list grows — and it does not keep it usable for long.
   * See the note under the control.
   */
  const available = listings
    .filter((listing) => !planned.has(listing.listingId))
    .map((listing) => ({ listing, blocked: blockedBecause(listing) }))
    .sort((left, right) => Number(left.blocked !== undefined) - Number(right.blocked !== undefined));
  const runnable = available.filter((entry) => entry.blocked === undefined).length;
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string; requestId?: string }>();

  if (available.length === 0) {
    return (
      <Text step="label" tone="fg.muted">
        Every listing you have is already on this lineup.
      </Text>
    );
  }

  return (
    <Card>
      <Stack gap="sm">
        <Select
          label="Add a lot"
          id="add-lot"
          value={selected}
          onChange={setSelected}
          options={[
            { value: '', label: 'Pick a listing…' },
            /*
              Annotated and disabled, not filtered. `shows.lineup.add` deliberately
              accepts a listing that is not auctionable *yet* — sellers plan a show and
              finish the listings afterwards — but "sold" and "taken down" are not "yet",
              they are "no". Disabling them keeps the seller from choosing something the
              engine will refuse, while leaving the reason where they were looking for it.
            */
            ...available.map(({ listing, blocked }) => ({
              value: listing.listingId,
              label: blocked ? `${listing.title} — ${blocked.toLowerCase()}` : listing.title,
              disabled: blocked !== undefined,
            })),
          ]}
          note={
            listings.length >= LISTING_PAGE
              ? `${runnable} ready to run. Showing your first ${LISTING_PAGE} listings — there is no search here yet.`
              : `${runnable} of ${available.length} ready to run. Adding a lot reserves nothing — the listing stays on sale until you put it on the block.`
          }
        />
        <Pressable
          variant="secondary"
          busy={busy}
          // Also guards the race: a lot selected a moment ago can sell before it is
          // added, and the option it came from is disabled by then.
          disabled={
            selected === '' || available.find((entry) => entry.listing.listingId === selected)?.blocked !== undefined
          }
          onPress={() => {
            setBusy(true);
            setError(undefined);
            void api()
              .command('shows.lineup.add', { showId, listingId: selected }, {})
              .then(async () => {
                setSelected('');
                await onAdded();
              })
              .catch((caught: unknown) => setError(describeError(caught)))
              .finally(() => setBusy(false));
          }}
        >
          Add to lineup
        </Pressable>
        {error ? <ErrorNote error={error} /> : null}
      </Stack>
    </Card>
  );
}

/** What happened to a lot, in the two lines a seller can read mid-show. */
function Outcome({ lot, running, skewMs }: { lot: Lot; running: boolean; skewMs: number }) {
  if (running) {
    return (
      <Stack gap="xs" align="end">
        <Text>
          {formatCents(lot.currentPriceCents)} · {lot.bidCount} {lot.bidCount === 1 ? 'bid' : 'bids'}
        </Text>
        <Text step="label" tone="fg.muted">
          on the block · <Countdown endTimeMs={lot.endTimeMs} skewMs={skewMs} closed={false} />
        </Text>
      </Stack>
    );
  }
  const won = lot.status === 'closed_won';
  return (
    <Stack gap="xs" align="end">
      <Text>
        {won
          ? `Sold ${formatCents(lot.clearingPriceCents ?? lot.currentPriceCents)}`
          : lot.status === 'cancelled'
            ? 'Cancelled'
            : 'No sale'}
      </Text>
      <Text step="label" tone="fg.muted">
        {won ? (lot.winnerDisplayName ?? 'winner') : `${lot.bidCount} ${lot.bidCount === 1 ? 'bid' : 'bids'}`}
      </Text>
    </Stack>
  );
}

/**
 * The room, from the seller's chair.
 *
 * **Plain rows rather than the viewer's overlay**: the seller is reading it while talking,
 * on a laptop, next to a lineup — legibility beats atmosphere here, and the artboard's
 * chat rail is designed to sit over video rather than beside a control panel.
 *
 * Every message carries its own remove. That is the whole reason this screen exists
 * alongside the capability: `shows.chat.remove` shipped with nothing able to call it, so a
 * seller could be moderated against and not moderate.
 */
function StudioChat({
  messages,
  onRemove,
}: {
  messages: ChatMessage[];
  onRemove: (message: ChatMessage) => Promise<void>;
}) {
  const [busy, setBusy] = useState<string>();

  return (
    <Stack gap="sm">
      <Text step="label" tone="fg.muted">
        CHAT
      </Text>
      {messages.length === 0 ? (
        <Text tone="fg.muted">Nothing said yet.</Text>
      ) : (
        <Stack gap="xs">
          {/* Newest last, and only the recent end: a seller mid-show is reading the bottom
              of the room, not scrolling its history. */}
          {messages.slice(-20).map((message) => (
            <Card key={message.messageId} padding="sm">
              <Stack direction="row" justify="between" align="center" gap="sm" wrap>
                <Text>
                  <Text step="heading">{message.displayName}</Text> {message.body}
                </Text>
                <Pressable
                  variant="quiet"
                  busy={busy === message.messageId}
                  onPress={() => {
                    setBusy(message.messageId);
                    void onRemove(message).finally(() => setBusy(undefined));
                  }}
                >
                  Remove
                </Pressable>
              </Stack>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
